import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Send, Check, Sparkles, Loader2 } from "lucide-react";
import type { Json } from "@/integrations/supabase/types";

interface Question {
  id: string;
  label: string;
  helper_text: string | null;
  input_type: string;
  sort_order: number;
  prompt_variable: string | null;
  options: Json;
  is_required: boolean;
  slider_min: number | null;
  slider_max: number | null;
  slider_labels: Json;
}

interface ChatMessage {
  id: string;
  role: "coach" | "user";
  content: string;
  questionIndex?: number;
}

interface ChatVisualizationFlowProps {
  questions: Question[];
  type: string;
  onSubmit: (answers: Record<string, any>) => void;
  onBack: () => void;
}

const COACH_INTROS: Record<string, string> = {
  competition: "Let's prepare your mind for competition day. I'll walk you through a few questions to create your perfect visualization.",
  practice: "Time to build a focused practice session. Let me learn a bit about what you're working on.",
  recovery: "Let's create a recovery session to help you reset and rebuild. I'll ask a few things to personalize it.",
};

const ChatVisualizationFlow = ({ questions, type, onSubmit, onBack }: ChatVisualizationFlowProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [isTyping, setIsTyping] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [pendingAnswer, setPendingAnswer] = useState<any>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, 100);
  }, []);

  // Initialize with intro message
  useEffect(() => {
    const intro = COACH_INTROS[type] || "Let's create your personalized visualization session.";
    setMessages([{ id: "intro", role: "coach", content: intro }]);
    
    // Show first question after delay
    const timer = setTimeout(() => {
      showQuestion(0);
    }, 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill slider defaults
  useEffect(() => {
    const defaults: Record<string, any> = {};
    for (const q of questions) {
      if (q.input_type === "slider" && q.prompt_variable) {
        const min = q.slider_min ?? 1;
        const max = q.slider_max ?? 10;
        defaults[q.prompt_variable] = Math.round((min + max) / 2);
      }
    }
    setAnswers((prev) => ({ ...defaults, ...prev }));
  }, [questions]);

  const showQuestion = (index: number) => {
    if (index >= questions.length) return;

    setIsTyping(true);
    scrollToBottom();

    setTimeout(() => {
      const q = questions[index];
      setMessages((prev) => [
        ...prev,
        {
          id: `q-${index}`,
          role: "coach",
          content: q.label,
          questionIndex: index,
        },
      ]);
      setCurrentQuestionIndex(index);
      setIsTyping(false);
      setPendingAnswer(null);
      setTextInput("");
      scrollToBottom();

      // Focus text input for text/textarea types
      setTimeout(() => inputRef.current?.focus(), 200);
    }, 800 + Math.random() * 400);
  };

  const submitAnswer = (value: any) => {
    const q = questions[currentQuestionIndex];
    if (!q) return;
    const variable = q.prompt_variable || q.id;

    // Build display text
    let displayText: string;
    if (Array.isArray(value)) {
      displayText = value.join(", ");
    } else {
      displayText = String(value);
    }

    // Add user message
    setMessages((prev) => [
      ...prev,
      { id: `a-${currentQuestionIndex}`, role: "user", content: displayText },
    ]);

    // Store answer
    const newAnswers = { ...answers, [variable]: value };
    setAnswers(newAnswers);
    scrollToBottom();

    // Check if this was the last question
    if (currentQuestionIndex >= questions.length - 1) {
      // Show generating state
      setIsTyping(true);
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { id: "closing", role: "coach", content: "Perfect — I have everything I need. Let me create your personalized session now..." },
        ]);
        setIsTyping(false);
        scrollToBottom();
        setTimeout(() => onSubmit(newAnswers), 1500);
      }, 1000);
    } else {
      showQuestion(currentQuestionIndex + 1);
    }
  };

  const handleTextSubmit = () => {
    const value = textInput.trim();
    if (!value) return;
    submitAnswer(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleTextSubmit();
    }
  };

  const currentQuestion = currentQuestionIndex >= 0 ? questions[currentQuestionIndex] : null;
  const isComplete = currentQuestionIndex >= questions.length;
  const options = currentQuestion?.options as string[] | null;
  const sliderLabels = currentQuestion?.slider_labels as Record<string, string> | null;

  const renderInputArea = () => {
    if (!currentQuestion || isComplete || isTyping) return null;

    const variable = currentQuestion.prompt_variable || currentQuestion.id;

    switch (currentQuestion.input_type) {
      case "text":
        return (
          <div className="flex gap-2 items-end">
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your response..."
              className="flex-1 h-11 rounded-xl border border-border/50 bg-secondary/30 px-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            />
            <Button
              size="icon"
              variant="hero"
              onClick={handleTextSubmit}
              disabled={!textInput.trim()}
              className="h-11 w-11 rounded-xl flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        );

      case "textarea":
        return (
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your response..."
              rows={2}
              className="flex-1 rounded-xl border border-border/50 bg-secondary/30 p-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none text-sm"
            />
            <Button
              size="icon"
              variant="hero"
              onClick={handleTextSubmit}
              disabled={!textInput.trim()}
              className="h-11 w-11 rounded-xl flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        );

      case "select":
      case "radio":
        return (
          <div className="flex flex-wrap gap-2">
            {options?.map((opt) => (
              <button
                key={opt}
                onClick={() => submitAnswer(opt)}
                className="px-4 py-2.5 rounded-xl border border-border/50 bg-secondary/30 text-sm text-foreground hover:border-primary/50 hover:bg-primary/10 transition-all"
              >
                {opt}
              </button>
            ))}
          </div>
        );

      case "multi_select": {
        const selected = (pendingAnswer || answers[variable] || []) as string[];
        return (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {options?.map((opt) => {
                const isSelected = selected.includes(opt);
                return (
                  <button
                    key={opt}
                    onClick={() => {
                      const newVal = isSelected
                        ? selected.filter((x) => x !== opt)
                        : [...selected, opt];
                      setPendingAnswer(newVal);
                    }}
                    className={`px-4 py-2.5 rounded-xl border text-sm flex items-center gap-2 transition-all ${
                      isSelected
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border/50 bg-secondary/30 text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "bg-primary border-primary" : "border-muted-foreground/40"
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    {opt}
                  </button>
                );
              })}
            </div>
            {selected.length > 0 && (
              <Button
                size="sm"
                variant="hero"
                onClick={() => submitAnswer(selected)}
                className="rounded-xl"
              >
                Confirm
                <Send className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            )}
          </div>
        );
      }

      case "slider": {
        const min = currentQuestion.slider_min ?? 1;
        const max = currentQuestion.slider_max ?? 10;
        const val = pendingAnswer ?? answers[variable] ?? Math.round((min + max) / 2);
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 space-y-2">
                <Slider
                  value={[val]}
                  onValueChange={(v) => setPendingAnswer(v[0])}
                  min={min}
                  max={max}
                  step={1}
                />
                {sliderLabels && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{sliderLabels[String(min)]}</span>
                    <span>{sliderLabels[String(max)]}</span>
                  </div>
                )}
              </div>
              <span className="font-display text-2xl font-bold text-gradient-primary w-10 text-center">
                {val}
              </span>
            </div>
            <Button
              size="sm"
              variant="hero"
              onClick={() => submitAnswer(val)}
              className="rounded-xl"
            >
              Confirm
              <Send className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen gradient-hero flex flex-col">
      {/* Header */}
      <header className="p-4 md:p-6 flex items-center gap-4 border-b border-border/30">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-display text-lg font-semibold capitalize">{type} Session</h1>
          <p className="text-xs text-muted-foreground">
            {currentQuestionIndex + 1} of {questions.length} questions
          </p>
        </div>
        <div className="w-24 h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full gradient-cta transition-all duration-500"
            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </header>

      {/* Chat messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-6">
        <div className="max-w-2xl mx-auto space-y-4">
          <AnimatePresence initial={false}>
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "coach" && (
                  <div className="w-8 h-8 rounded-full gradient-cta flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                    <Sparkles className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === "coach"
                      ? "bg-secondary/50 border border-border/30 text-foreground"
                      : "gradient-cta text-primary-foreground"
                  }`}
                >
                  {msg.content}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing indicator */}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start"
            >
              <div className="w-8 h-8 rounded-full gradient-cta flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                <Sparkles className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="bg-secondary/50 border border-border/30 rounded-2xl px-4 py-3">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </motion.div>
          )}

          {/* Helper text for current question */}
          {currentQuestion?.helper_text && !isTyping && !isComplete && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xs text-muted-foreground pl-11"
            >
              {currentQuestion.helper_text}
            </motion.p>
          )}
        </div>
      </div>

      {/* Input area */}
      <div className="border-t border-border/30 p-4 md:p-6">
        <div className="max-w-2xl mx-auto">
          {renderInputArea()}
        </div>
      </div>
    </div>
  );
};

export default ChatVisualizationFlow;
